import React from "react";
import { createPortal } from "react-dom";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ActivityTableColumnPreference,
  dedupeActivityTableColumns,
} from "../../utils/activityTableColumns";

type ActivityTableConfiguratorProps = {
  title: string;
  description: string;
  isOpen: boolean;
  selectedColumns: ActivityTableColumnPreference[];
  defaultColumns: ActivityTableColumnPreference[];
  formColumns: ActivityTableColumnPreference[];
  selectedColumnsTitle?: string;
  defaultColumnsTitle?: string;
  formColumnsTitle?: string;
  defaultColumnKindLabel?: string;
  formColumnKindLabel?: string;
  emptyColumnsLabel?: string;
  emptySelectionLabel?: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (columns: ActivityTableColumnPreference[]) => void;
};

const hasColumn = (
  selectedColumns: ActivityTableColumnPreference[],
  target: ActivityTableColumnPreference,
): boolean =>
  selectedColumns.some(
    (column) =>
      column.kind === target.kind &&
      column.id === target.id &&
      column.label === target.label,
  );

export const ActivityTableConfigurator: React.FC<
  ActivityTableConfiguratorProps
> = ({
  title,
  description,
  isOpen,
  selectedColumns,
  defaultColumns,
  formColumns,
  selectedColumnsTitle = "Seçili Kolonlar",
  defaultColumnsTitle = "Varsayılan Kolonlar",
  formColumnsTitle = "Form Kolonları",
  defaultColumnKindLabel = "Sabit kolon",
  formColumnKindLabel = "Form kolonu",
  emptyColumnsLabel = "Kolon bulunamadı.",
  emptySelectionLabel = "En az bir kolon seçin.",
  saving = false,
  onClose,
  onSave,
}) => {
  const [draftColumns, setDraftColumns] = React.useState<
    ActivityTableColumnPreference[]
  >(selectedColumns);

  React.useEffect(() => {
    if (isOpen) {
      setDraftColumns(selectedColumns);
    }
  }, [isOpen, selectedColumns]);

  if (!isOpen) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  const toggleColumn = (column: ActivityTableColumnPreference) => {
    setDraftColumns((current) => {
      const exists = hasColumn(current, column);
      if (exists) {
        return current.filter(
          (item) =>
            !(
              item.kind === column.kind &&
              item.id === column.id &&
              item.label === column.label
            ),
        );
      }
      return dedupeActivityTableColumns([...current, column]);
    });
  };

  const moveColumn = (index: number, direction: -1 | 1) => {
    setDraftColumns((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const renderColumnPicker = (
    heading: string,
    columns: ActivityTableColumnPreference[],
  ) => (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {heading}
      </p>
      <div className="mt-3 max-h-52 space-y-2 overflow-y-auto pr-1">
        {columns.length === 0 ? (
          <p className="text-sm text-gray-500">{emptyColumnsLabel}</p>
        ) : (
          columns.map((column) => {
            const checked = hasColumn(draftColumns, column);
            return (
              <label
                key={`${column.kind}:${column.id}`}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleColumn(column)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="truncate">{column.label}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-x-0 top-24 z-[140] flex justify-end px-4 pointer-events-none">
      <div className="pointer-events-auto w-[min(92vw,760px)] max-h-[min(80vh,720px)] overflow-y-auto rounded-xl border border-gray-200 bg-slate-50 p-4 shadow-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        <div className="rounded-lg border border-blue-100 bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
            {selectedColumnsTitle}
          </p>
          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
            {draftColumns.length === 0 ? (
              <p className="text-sm text-gray-500">{emptySelectionLabel}</p>
            ) : (
              draftColumns.map((column, index) => (
                <div
                  key={`${column.kind}:${column.id}:${index}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {column.label}
                    </p>
                    <p className="text-xs text-gray-500">
                      {column.kind === "form"
                        ? formColumnKindLabel
                        : defaultColumnKindLabel}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveColumn(index, -1)}
                      disabled={index === 0}
                      className="rounded-md p-1 text-gray-500 hover:bg-white hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveColumn(index, 1)}
                      disabled={index === draftColumns.length - 1}
                      className="rounded-md p-1 text-gray-500 hover:bg-white hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-4">
          {renderColumnPicker(defaultColumnsTitle, defaultColumns)}
          {renderColumnPicker(formColumnsTitle, formColumns)}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-white"
        >
          Vazgeç
        </button>
        <button
          type="button"
          disabled={saving || draftColumns.length === 0}
          onClick={() => onSave(draftColumns)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
      </div>
    </div>,
    document.body,
  );
};
